/**
 * Session Cache
 *
 * Lightweight auth session/profile cache backed by localStorage.
 * Maintains the same API as the old CacheManager-backed implementation.
 *
 * Uses:
 * - localStorage for auth session (persistent across tabs)
 * - Simple TTL-based expiration
 */

import { Tables } from '@/types/database';

type Profile = Tables<'user_profiles'>;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class SessionCache {
  private readonly SESSION_KEY = 'auth_session_cache';
  private readonly PROFILE_KEY_PREFIX = 'auth_profile_cache_';
  private readonly PROFILE_TTL = 300000; // 5 minutes - short TTL to prevent stale profile data

  private getItem<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;

      const entry: CacheEntry<T> = JSON.parse(raw);
      if (Date.now() > entry.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }
      return entry.data;
    } catch {
      return null;
    }
  }

  private setItem<T>(key: string, data: T, ttl: number): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + ttl,
      };
      localStorage.setItem(key, JSON.stringify(entry));
    } catch {
      // Quota exceeded or other storage error – silently ignore
    }
  }

  private removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore
    }
  }

  /**
   * Get cached profile for a user
   */
  getProfile(userId: string): Profile | null {
    const key = `${this.PROFILE_KEY_PREFIX}${userId}`;
    return this.getItem<Profile>(key);
  }

  /**
   * Set profile in cache
   */
  setProfile(profile: Profile | null, userId: string): void {
    const key = `${this.PROFILE_KEY_PREFIX}${userId}`;

    if (profile) {
      this.setItem(key, profile, this.PROFILE_TTL);
    } else {
      this.removeItem(key);
    }
  }

  /**
   * Clear all caches (on logout)
   */
  clear(): void {
    // Sessions are no longer cached (the browser never holds the refresh
    // token); remove what older versions stored – it contained both tokens.
    this.removeItem(this.SESSION_KEY);

    // Clear all profiles by scanning localStorage keys
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.PROFILE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch {
      // Ignore
    }
  }

  /**
   * Check if we have a valid cached profile
   */
  hasProfile(userId: string): boolean {
    return this.getProfile(userId) !== null;
  }

  /**
   * Invalidate profile cache for a specific user
   */
  invalidateProfile(userId: string): void {
    const key = `${this.PROFILE_KEY_PREFIX}${userId}`;
    this.removeItem(key);
  }
}

// Singleton instance
export const sessionCache = new SessionCache();
